import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const photos = [
  {
    src: '/assets/galeria/ganador.jpg',
    alt: 'Estudiante ganador en competencia tecnológica',
    caption: 'Excelencia y premios en competencias',
  },
  {
    src: '/assets/galeria/todos_ieee.jpg',
    alt: 'Actividades colectivas de IEEE ESPOCH',
    caption: 'Convenciones y encuentros institucionales',
  },
  {
    src: '/assets/galeria/galeria_1.jpg',
    alt: 'Sesión de trabajo colaborativo',
    caption: 'Equipo trabajando en proyectos tecnológicos',
  },
  {
    src: '/assets/galeria/galeria_2.jpg',
    alt: 'Presentación técnica en evento',
    caption: 'Innovación, investigación y audiencia técnica',
  },
  {
    src: '/assets/galeria/galeria_3.jpg',
    alt: 'Foto de la comunidad IEEE ESPOCH',
    caption: 'Comunidad académica y colaboración local',
  },
  {
    src: '/assets/galeria/galeria_4.png',
    alt: 'Proyecto tecnológico en desarrollo',
    caption: 'Diseño y prototipado tecnológico',
  },
  {
    src: '/assets/galeria/imagen1.png',
    alt: 'Momento destacado de la comunidad',
    caption: 'Interacción y trabajo en equipo',
  },
  {
    src: '/assets/galeria/imagen2.jpeg',
    alt: 'Reunión y participación activa',
    caption: 'Participación en eventos IEEE',
  },
];

const Gallery: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  useEffect(() => {
    if (selectedIndex === null) {
      document.body.style.overflow = 'auto';
      return;
    }

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedIndex(null);
      }
      if (event.key === 'ArrowLeft') {
        setSelectedIndex((current) => (current === null ? null : (current - 1 + photos.length) % photos.length));
      }
      if (event.key === 'ArrowRight') {
        setSelectedIndex((current) => (current === null ? null : (current + 1) % photos.length));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [selectedIndex]);

  const openPhoto = (index: number) => setSelectedIndex(index);
  const closePhoto = () => setSelectedIndex(null);
  const prevPhoto = () => setSelectedIndex((current) => (current === null ? null : (current - 1 + photos.length) % photos.length));
  const nextPhoto = () => setSelectedIndex((current) => (current === null ? null : (current + 1) % photos.length));

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(event.touches[0].clientX);
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX === null) return;
    const deltaX = event.changedTouches[0].clientX - touchStartX;
    if (deltaX > 40) prevPhoto();
    if (deltaX < -40) nextPhoto();
    setTouchStartX(null);
  };

  return (
    <section className="relative overflow-hidden bg-slate-50 py-24" id="gallery">
      {/* Background ambient lighting */}
      <div className="absolute left-0 top-20 h-64 w-64 rounded-full bg-indigo-100/40 blur-3xl pointer-events-none" />
      <div className="absolute right-0 bottom-16 h-72 w-72 rounded-full bg-cyan-100/40 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20 space-y-4"
        >
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-slate-500">Galería</p>
          <h2 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight">
            Momentos clave de <span className="text-indigo-600">IEEE ESPOCH</span>
          </h2>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-slate-600 font-normal">
            Imágenes oficiales que reflejan la energía, el estilo y el impacto de nuestra comunidad académica.
          </p>
        </motion.div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {photos.map((photo, index) => (
            <motion.button
              key={photo.src}
              type="button"
              onClick={() => openPhoto(index)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.08 }}
              whileHover={{ y: -8 }}
              className="group overflow-hidden rounded-[2.5rem] border border-slate-100 bg-white p-0 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-indigo-100"
            >
              <div className="relative overflow-hidden aspect-[4/3]">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="space-y-2 p-8 text-left">
                <p className="text-lg font-black text-slate-900 leading-tight">{photo.caption}</p>
                <p className="text-sm text-slate-500 font-normal">{photo.alt}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            onClick={closePhoto}
            className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-xl px-4 py-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(event) => event.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              className="relative w-full max-w-5xl rounded-[2.5rem] bg-white border border-slate-100 shadow-xl overflow-hidden"
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.3 }}
            >
              <button
                type="button"
                onClick={closePhoto}
                className="absolute right-6 top-6 z-20 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 bg-white/50 text-slate-900 transition hover:bg-white hover:shadow-sm backdrop-blur-md"
                aria-label="Cerrar galería"
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>

              <div className="relative flex flex-col md:flex-row">
                <div className="relative flex-1 bg-slate-100 overflow-hidden">
                  <img
                    src={photos[selectedIndex].src}
                    alt={photos[selectedIndex].alt}
                    className="h-[60vh] md:h-[80vh] w-full object-contain"
                  />
                </div>
                
                <div className="w-full md:w-80 p-8 flex flex-col justify-between bg-white border-t md:border-t-0 md:border-l border-slate-100">
                  <div className="space-y-6">
                    <p className="text-xs font-black uppercase tracking-[0.32em] text-slate-400">Galería IEEE</p>
                    <div className="space-y-3">
                      <h3 className="text-2xl font-black text-slate-900 leading-tight">{photos[selectedIndex].caption}</h3>
                      <p className="text-slate-600 font-normal leading-relaxed">{photos[selectedIndex].alt}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-8">
                    <button
                      type="button"
                      onClick={prevPhoto}
                      className="flex-1 inline-flex items-center justify-center h-12 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-900 transition hover:bg-slate-50 active:scale-95"
                    >
                      Anterior
                    </button>
                    <button
                      type="button"
                      onClick={nextPhoto}
                      className="flex-1 inline-flex items-center justify-center h-12 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-900 transition hover:bg-slate-50 active:scale-95"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
